/**
 * CorveeMemberPanel — zelfstandig blok dat de standaard corvee-beschikbaarheid
 * (even/oneven) van één persoon toont/bewerkt, plus optioneel de credits.
 * Resolvet een e-mailadres naar het Airtable person_id (recXXX) en praat met
 * /Api/corvee/*. Gebruikt in /mijn-profiel én in het admin-gebruikersscherm
 * (edit-window = bewerkbaar, uitklap = read-only).
 */
import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import { BACKEND_URL } from "../../utils/apiConfig";
import { authFetch } from "../../utils/authHelpers";
import { leesJson, meldFout } from "../../utils/apiFout";
import { fullWeek } from "./corveeLogic";
import PatternEditor from "./PatternEditor";

const T = {
  primary: "#526075", text: "#2d3335", secondary: "#556071", outline: "#767c7e", outlineVariant: "#adb3b5",
  surface: "#ffffff", surfaceLow: "#f1f4f5", surfaceHigh: "#e5e9eb",
  emerald: "#10b981", emeraldBg: "#ecfdf5", error: "#a83836", errorBg: "#fef2f2",
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
};
const API = `${BACKEND_URL}/Api/corvee`;
const cGet = (p) => authFetch(`${API}${p}`).then((r) => leesJson(r, `Corvee laden (${p})`));
const cPut = (p, b) => authFetch(`${API}${p}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(b) }).then((r) => leesJson(r, `Corvee opslaan (${p})`));

export default function CorveeMemberPanel({ email, readOnly = false, showCredits = false }) {
  const [state, setState] = useState({ loading: true, personId: null, name: null, workdays: null, pattern: { pattern_even: fullWeek(), pattern_odd: fullWeek() }, history: 0, shirks: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        // @full-frame.nl en @fullframe.nl zijn dezelfde gebruiker.
        const norm = (e) => (e || "").trim().toLowerCase().replace("@full-frame.nl", "@fullframe.nl");
        const lower = norm(email);
        const [teamRes, members] = await Promise.all([cGet("/team"), cGet("/members")]);
        const me = (teamRes.team || []).find((p) => norm(p.email) === lower);
        if (cancelled) return;
        if (!me) { setState({ loading: false, personId: null, name: null, workdays: null, pattern: { pattern_even: fullWeek(), pattern_odd: fullWeek() }, history: 0, shirks: 0 }); return; }
        const mr = (Array.isArray(members) ? members : []).find((m) => m.person_id === me.id);
        setState({
          loading: false, personId: me.id, name: me.name, workdays: me.workdays || null,
          pattern: { pattern_even: mr?.pattern_even?.length ? mr.pattern_even : fullWeek(), pattern_odd: mr?.pattern_odd?.length ? mr.pattern_odd : fullWeek() },
          history: mr?.history || 0, shirks: mr?.shirks || 0,
        });
      } catch (e) { console.error("[Corvee] member panel:", e); if (!cancelled) { meldFout(e, "Corvee-profiel laden"); setState((s) => ({ ...s, loading: false })); } }
    })();
    return () => { cancelled = true; };
  }, [email]);

  const onChange = (next) => {
    setState((s) => ({ ...s, pattern: next }));
    if (state.personId) cPut(`/members/${state.personId}`, { pattern_even: next.pattern_even, pattern_odd: next.pattern_odd, display_name: state.name }).catch((e) => { console.error(e); toast.error("Opslaan beschikbaarheid mislukt"); });
  };

  if (state.loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={22} /></Box>;
  if (!state.personId) return <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.78rem", color: T.outlineVariant }}>Geen corvee-profiel — alleen medewerkers en stagiairs doen mee.</Typography>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PatternEditor value={state.pattern} workdays={state.workdays} onChange={onChange} readOnly={readOnly} />
      {showCredits && (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Box sx={{ borderRadius: "0.6rem", border: `1px solid ${T.surfaceHigh}`, px: 2, py: 1.25, minWidth: 120 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.66rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.outline }}>Saldo</Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: "1.3rem", fontWeight: 800, color: state.history < 0 ? T.error : T.emerald }}>{state.history > 0 ? "+" : ""}{state.history}</Typography>
          </Box>
          <Box sx={{ borderRadius: "0.6rem", border: `1px solid ${T.surfaceHigh}`, px: 2, py: 1.25, minWidth: 120 }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: "0.66rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.outline }}>Overgeslagen</Typography>
            <Typography sx={{ fontFamily: T.font, fontSize: "1.3rem", fontWeight: 800, color: state.shirks > 0 ? T.error : T.text }}>{state.shirks}×</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
